//
// Use selected OR selection
//
// name: the radio button group name
// id: this very button's identifier
//

import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useCallback,
} from 'react';

import { appTooltipProps } from '../../utils';
import {
  Column,
  Row,
} from '../display';
import SvgColorImg from '../SvgColorImg';

function JoystickButton(props) {
  const {
    className = 'h-3 w-3 shrink-0 relative',
    position,
    disabled,
    onClick,
  } = props;

  return (
    <button
      onClick={onClick}
      id={position}
      type="button"
      aria-label={position}
      disabled={disabled}
      className={className}
    >
      <SvgColorImg
        src={(['center', 'hCenter', 'vCenter'].includes(position))
          ? '/images/svg/gui/square.svg'
          : '/images/svg/gui/triangle.svg'}
        alt={position}
        imgColorAsCssFill
        className={classNames(
          'w-full h-full',
          {
            'fill-pxPrimary': !disabled,
            'fill-black opacity-60': disabled,
            'rotate-180': position === 'bottom',
            '-rotate-90': position === 'left',
            'rotate-90': position === 'right',
          },
        )}
      />
    </button>
  );
}

JoystickButton.propTypes = {
  className: PropTypes.string,
  position: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
};

function Joystick(props) {
  const {
    // static, changes are ignored:
    id,
    name,
    // dynamic (changes are reflected):
    className,
    onClick,
    disabled,
    split,
    horizontal,
    vertical,
    // Tooltip shown on hover
    tooltip,
  } = props;

  const handleClick = useCallback(
    (event) => {
      onClick({
        currentTarget: {
          id,
          name,
          value: event.currentTarget.id,
        },
      });
    },
    [id, name, onClick],
  );

  if (horizontal) {
    return (
      <div {...appTooltipProps(tooltip)} className={classNames('shrink-0', className)}>
        <Column id={id} name={name} className="h-3 w-10" gap={0.5} crossStart>
          <div className="flex-1" />
          <Row className="overflow-hidden flex-1" gap={0.5} crossStretch>
            <JoystickButton
              position="left"
              onClick={handleClick}
              disabled={disabled}
            />
            <JoystickButton
              position="hCenter"
              onClick={handleClick}
              disabled={disabled}
            />
            <JoystickButton
              position="right"
              onClick={handleClick}
              disabled={disabled}
            />
          </Row>
          <div className="flex-1" />
        </Column>
      </div>
    );
  }

  if (vertical) {
    return (
      <div {...appTooltipProps(tooltip)} className={classNames('shrink-0', className)}>
        <Column id={id} name={name} className="h-10 w-3" gap={0.5} crossEnd>
          <JoystickButton
            position="top"
            onClick={handleClick}
            disabled={disabled}
          />
          <JoystickButton
            position="vCenter"
            onClick={handleClick}
            disabled={disabled}
          />
          <JoystickButton
            position="bottom"
            onClick={handleClick}
            disabled={disabled}
          />
        </Column>
      </div>
    );
  }

  if (split) {
    return (
      <div {...appTooltipProps(tooltip)} className={classNames('shrink-0', className)}>
        <Row id={id} name={name} className="overflow-hidden" crossStretch between gap={0}>
          <Column id="horizontal" className="" gap={0.5} crossStart>
            <div className="flex-1" />
            <Row className="shrink-0 h-3 w-10" gap={0.5} crossStretch>
              <JoystickButton
                position="left"
                onClick={handleClick}
                disabled={disabled}
              />
              <JoystickButton
                position="hCenter"
                onClick={handleClick}
                disabled={disabled}
              />
              <JoystickButton
                position="right"
                onClick={handleClick}
                disabled={disabled}
              />
            </Row>
            <div className="flex-1" />
          </Column>

          <Column id="vertical" className="shrink-0 h-10 w-3" gap={0.5} crossEnd>
            <JoystickButton
              position="top"
              onClick={handleClick}
              disabled={disabled}
            />
            <JoystickButton
              position="vCenter"
              onClick={handleClick}
              disabled={disabled}
            />
            <JoystickButton
              position="bottom"
              onClick={handleClick}
              disabled={disabled}
            />
          </Column>
        </Row>
      </div>
    );
  }

  return (
    <div {...appTooltipProps(tooltip)} className={classNames('shrink-0', className)}>
      <Row id={id} name={name} className="overflow-hidden" gap={0.5}>
        <Column id="leftCol" className="shrink-0 h-10 w-3" gap={0.5}>
          <div className="flex-1" />
          <JoystickButton
            position="left"
            onClick={handleClick}
            disabled={disabled}
          />
          <div className="flex-1" />
        </Column>
        <Column id="centerCol" className="shrink-0 h-10 w-3" gap={0.5}>
          <JoystickButton
            position="top"
            onClick={handleClick}
            disabled={disabled}
          />
          <JoystickButton
            position="center"
            onClick={handleClick}
            disabled={disabled}
          />
          <JoystickButton
            position="bottom"
            onClick={handleClick}
            disabled={disabled}
          />
        </Column>
        <Column id="rightCol" className="shrink-0 h-10 w-3" gap={0.5}>
          <div className="flex-1" />
          <JoystickButton
            position="right"
            onClick={handleClick}
            disabled={disabled}
          />
          <div className="flex-1" />
        </Column>
      </Row>
    </div>
  );
}

Joystick.propTypes = {
  className: PropTypes.string,
  id: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  name: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  split: PropTypes.bool,
  horizontal: PropTypes.bool,
  vertical: PropTypes.bool,
  tooltip: PropTypes.string,
};

export default Joystick;
